import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { TenantUserListPhoto } from '../../features/user/components/UserListPhoto';
import {
  formatDirectoryUserDisplayName,
  formatInitials,
} from '../../features/user/userDisplay';
import { useI18n } from '../../i18n/useI18n';
import type { AssignedDirectoryUser } from '../../types/user';
import type { DirectoryUser } from '../../types/user';
import './TenantResourceUserAssignments.css';

export type TenantAssignableUser = DirectoryUser & { id: string };

type ResolvedAssignedUser = {
  /** Assigned member Keycloak id (row key + unassign/assign path param). */
  userId: string;
  displayName: string;
  email: string | null;
  thumbnail: Blob | string | null;
};

export type TenantResourceUserAssignmentsProps = {
  assignedUsers: ReadonlyArray<AssignedDirectoryUser>;
  directoryUsers: ReadonlyArray<TenantAssignableUser>;
  isLoading?: boolean;
  isError?: boolean;
  pendingUserId?: string | null;
  onAssign: (userId: string) => void;
  onUnassign: (userId: string) => void;
};

function formatAssignedDisplayName(
  assigned: AssignedDirectoryUser,
  unknownUserLabel: string,
): string {
  const fullName = [assigned.firstName?.trim(), assigned.lastName?.trim()]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  return (
    fullName ||
    assigned.name?.trim() ||
    assigned.email?.trim() ||
    unknownUserLabel
  );
}

/**
 * Prefer directory match by membership `id` (assigned member Keycloak id).
 * Fall back to email when the directory id is missing or stale.
 */
function resolveDirectoryUserForAssigned(
  assigned: AssignedDirectoryUser,
  directoryById: Map<string, TenantAssignableUser>,
  directoryByEmail: Map<string, TenantAssignableUser>,
): TenantAssignableUser | undefined {
  const byId = directoryById.get(assigned.id);
  if (byId) {
    return byId;
  }

  const emailKey = assigned.email?.trim().toLowerCase() ?? '';
  if (emailKey !== '') {
    return directoryByEmail.get(emailKey);
  }

  return undefined;
}

function resolveAssignedUser(
  assigned: AssignedDirectoryUser,
  directoryById: Map<string, TenantAssignableUser>,
  directoryByEmail: Map<string, TenantAssignableUser>,
  unknownUserLabel: string,
): ResolvedAssignedUser {
  const directoryUser = resolveDirectoryUserForAssigned(
    assigned,
    directoryById,
    directoryByEmail,
  );

  return {
    userId: directoryUser?.id ?? assigned.id,
    displayName: formatAssignedDisplayName(assigned, unknownUserLabel),
    email: assigned.email?.trim() || directoryUser?.email?.trim() || null,
    thumbnail: directoryUser?.thumbnail ?? assigned.thumbnail ?? null,
  };
}

export function TenantResourceUserAssignments({
  assignedUsers,
  directoryUsers,
  isLoading = false,
  isError = false,
  pendingUserId = null,
  onAssign,
  onUnassign,
}: TenantResourceUserAssignmentsProps) {
  const { translate } = useI18n();
  const [selectedUserId, setSelectedUserId] = useState('');
  const unknownUserLabel = translate(
    'organisationManagement.users.unknownUser',
  );

  const directoryById = useMemo(() => {
    const map = new Map<string, TenantAssignableUser>();
    for (const user of directoryUsers) {
      map.set(user.id, user);
    }
    return map;
  }, [directoryUsers]);

  const directoryByEmail = useMemo(() => {
    const map = new Map<string, TenantAssignableUser>();
    for (const user of directoryUsers) {
      const email = user.email?.trim().toLowerCase();
      if (email) {
        map.set(email, user);
      }
    }
    return map;
  }, [directoryUsers]);

  const resolvedAssignedUsers = useMemo(() => {
    const unique = new Map<string, ResolvedAssignedUser>();
    for (const assigned of assignedUsers) {
      const resolved = resolveAssignedUser(
        assigned,
        directoryById,
        directoryByEmail,
        unknownUserLabel,
      );
      unique.set(resolved.userId, resolved);
    }
    return [...unique.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [assignedUsers, directoryByEmail, directoryById, unknownUserLabel]);

  const assignedDirectoryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const assigned of assignedUsers) {
      ids.add(assigned.id);
      const directoryUser = resolveDirectoryUserForAssigned(
        assigned,
        directoryById,
        directoryByEmail,
      );
      if (directoryUser) {
        ids.add(directoryUser.id);
      }
    }
    return ids;
  }, [assignedUsers, directoryByEmail, directoryById]);

  const candidateOptions = useMemo(
    () =>
      directoryUsers
        .filter((user) => !assignedDirectoryIds.has(user.id))
        .map((user) => ({
          value: user.id,
          label: formatDirectoryUserDisplayName(user, unknownUserLabel),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [assignedDirectoryIds, directoryUsers, unknownUserLabel],
  );

  if (isLoading) {
    return (
      <p className="tenant-labels-groups__status">
        {translate('organisationManagement.labelsAndGroups.usersLoading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="tenant-labels-groups__status" role="alert">
        {translate('organisationManagement.labelsAndGroups.usersLoadError')}
      </p>
    );
  }

  const selectedIsAssigned =
    selectedUserId !== '' && assignedDirectoryIds.has(selectedUserId);

  return (
    <div className="tenant-labels-groups__assignments">
      <div className="tenant-labels-groups__assign-row">
        <LiksDropdown
          className="tenant-labels-groups__assign-dropdown"
          variant="on-light"
          value={selectedIsAssigned ? '' : selectedUserId}
          onChange={setSelectedUserId}
          options={candidateOptions}
          placeholder={translate(
            'organisationManagement.labelsAndGroups.selectUserPlaceholder',
          )}
          filterable
          filterPlaceholder={translate(
            'organisationManagement.labelsAndGroups.selectUserPlaceholder',
          )}
          filterAriaLabel={translate(
            'organisationManagement.labelsAndGroups.searchUsersLabel',
          )}
          filterEmptyLabel={translate(
            'organisationManagement.labelsAndGroups.filterUsersEmpty',
          )}
          filterClearLabel={translate('common.search.clear')}
          aria-label={translate(
            'organisationManagement.labelsAndGroups.selectUserLabel',
          )}
          disabled={candidateOptions.length === 0}
        />
        <LiksButton
          type="button"
          disabled={
            selectedUserId === '' ||
            selectedIsAssigned ||
            pendingUserId === selectedUserId ||
            candidateOptions.length === 0
          }
          onClick={() => {
            if (
              selectedUserId === '' ||
              assignedDirectoryIds.has(selectedUserId)
            ) {
              return;
            }
            onAssign(selectedUserId);
            setSelectedUserId('');
          }}
        >
          <Plus strokeWidth={2} aria-hidden />
          {translate('organisationManagement.labelsAndGroups.addUser')}
        </LiksButton>
      </div>

      {resolvedAssignedUsers.length === 0 ? (
        <p className="tenant-labels-groups__empty">
          {translate('organisationManagement.labelsAndGroups.usersEmpty')}
        </p>
      ) : (
        <ul className="tenant-labels-groups__user-list">
          {resolvedAssignedUsers.map((user) => {
            const isPending = pendingUserId === user.userId;
            return (
              <li key={user.userId} className="tenant-labels-groups__user-row">
                <TenantUserListPhoto
                  initials={formatInitials(user.displayName)}
                  thumbnail={user.thumbnail}
                />
                <div className="tenant-labels-groups__user-meta">
                  <span className="tenant-labels-groups__user-name">
                    {user.displayName}
                  </span>
                  {user.email ? (
                    <span className="tenant-labels-groups__user-email">
                      {user.email}
                    </span>
                  ) : null}
                </div>
                <LiksButton
                  type="button"
                  variant="secondary"
                  disabled={isPending}
                  onClick={() => onUnassign(user.userId)}
                  aria-label={translate(
                    'organisationManagement.labelsAndGroups.removeUser',
                    { name: user.displayName },
                  )}
                >
                  <X strokeWidth={2} aria-hidden />
                  {translate('organisationManagement.labelsAndGroups.remove')}
                </LiksButton>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
